<template>
    <transition name="menu-transition-modal">
        <div
            class="app-menu"
            v-bind:class="{ 'app-menu--testnet': IS_TESTNET }"
            v-if="isOpen || keepMarkup"
            v-bind:style="{ transform: `translateX(${currentX}px)`, transition: transitionProperty }"
            v-on:touchstart="handleTouchStart"
            v-on:touchmove="handleTouchMove"
            v-on:touchend="handleTouchEnd"
        >
            <div class="app-menu__scroll">
                <nav class="app-menu-group">
                    <ui-link
                        v-for="link in links"
                        v-bind:key="link"
                        class="app-menu-item"
                        v-bind:class="{ 'app-menu-item--active': isActive(link) }"
                        v-bind:to="{ name: link }"
                        v-on:click.native="closeModal"
                    >
                        <span class="app-menu-item__icon">
                            <component v-bind:is="icons[link]"/>
                        </span>
                        <span class="app-menu-item__label">{{ $t(`header.links.${link}`) }}</span>
                        <icon-chevron class="app-menu-item__chevron"/>
                    </ui-link>

                    <!-- The rest of the sections fold under one «More» row: -->
                    <template v-if="!IS_TESTNET">
                        <button type="button"
                            class="app-menu-item app-menu-item--toggle"
                            v-bind:class="{ 'app-menu-item--open': isMoreOpen }"
                            v-bind:aria-expanded="isMoreOpen ? 'true' : 'false'"
                            v-on:click="isMoreOpen = !isMoreOpen"
                        >
                            <span class="app-menu-item__icon">
                                <icon-more/>
                            </span>
                            <span class="app-menu-item__label">{{ $t('header.links.more') }}</span>
                            <icon-chevron class="app-menu-item__chevron app-menu-item__chevron--toggle"/>
                        </button>

                        <div class="app-menu-more" v-bind:class="{ 'app-menu-more--open': isMoreOpen }">
                            <div class="app-menu-more__inner">
                                <ui-link
                                    v-for="link in moreLinks"
                                    v-bind:key="link"
                                    class="app-menu-item"
                                    v-bind:class="{ 'app-menu-item--active': isActive(link) }"
                                    v-bind:to="{ name: link }"
                                    v-bind:tabindex="isMoreOpen ? undefined : -1"
                                    v-on:click.native="closeModal"
                                >
                                    <span class="app-menu-item__icon">
                                        <component v-bind:is="icons[link]"/>
                                    </span>
                                    <span class="app-menu-item__label">{{ $t(`header.links.${link}`) }}</span>
                                    <icon-chevron class="app-menu-item__chevron"/>
                                </ui-link>
                            </div>
                        </div>
                    </template>
                </nav>

                <div class="app-menu-group__label">{{ $t('settings.title') }}</div>
                <div class="app-menu-group">
                    <app-settings compact/>
                </div>
            </div>
        </div>
    </transition>
</template>

<script>
import IconHome from '@img/icons/filled/house.svg?vue';
import IconStats from '@img/icons/filled/chart-bars.svg?vue';
import IconTokens from '@img/icons/filled/coins.svg?vue';
import IconApps from '@img/icons/filled/apps.svg?vue';
import IconBlocks from '@img/icons/filled/cube.svg?vue';
import IconWhales from '@img/icons/filled/trophy.svg?vue';
import IconValidators from '@img/icons/filled/nodes.svg?vue';
import IconConfig from '@img/icons/filled/sliders.svg?vue';
import IconTools from '@img/icons/filled/wrench.svg?vue';
import IconMore from '@img/icons/filled/ellipsis.svg?vue';
import IconChevron from '@img/icons/lucide/chevron-right.svg?vue';
import { IS_TESTNET } from '~/config';
import AppSettings from '~/components/settings/AppSettings.vue';

// Only a drag that starts at the left edge is a "swipe to close":
const SWIPE_EDGE = 60;

export default {
    props: {
        isOpen: {
            type: Boolean,
            default: false,
        },
        keepMarkup: {
            type: Boolean,
            default: false,
        },
    },

    data() {
        return {
            startX: 0,
            currentX: 0,
            transitionProperty: '',
            isMoreOpen: false,
            icons: Object.freeze({
                index: IconHome,
                stats: IconStats,
                market: IconTokens,
                apps: IconApps,
                blocks: IconBlocks,
                whales: IconWhales,
                validators: IconValidators,
                config: IconConfig,
                tools: IconTools,
            }),
        };
    },

    computed: {
        links() {
            if (IS_TESTNET) {
                return [
                    'index',
                    'blocks',
                    'validators',
                    'whales',
                ];
            }

            return [
                'index',
                'stats',
                'blocks',
                'apps',
            ];
        },

        moreLinks() {
            return ['market', 'validators', 'whales', 'config', 'tools'];
        },

        IS_TESTNET() {
            return IS_TESTNET;
        },
    },

    watch: {
        // A page from the folded part keeps its row in sight:
        '$route.name': {
            immediate: true,
            handler() {
                if (this.moreLinks.some(link => this.isActive(link))) {
                    this.isMoreOpen = true;
                }
            },
        },
    },

    methods: {
        isActive(link) {
            const route = this.$route.name || '';
            if (link === 'index') return route === 'index';
            if (link === 'apps') return route === 'apps' || route === 'app';
            if (link === 'blocks') return route.startsWith('block');
            return route.startsWith(link);
        },

        closeModal() {
            this.$emit('update:isOpen', false);
            this.$emit('modal-close');
        },

        // Swipe from the left edge drags the sheet along and closes it past 80px.
        // Any other touch is a plain scroll of the menu contents.
        handleTouchStart(event) {
            this.startX = event.touches[0].clientX;
            this.transitionProperty = '';
            this.currentX = 0;
        },
        handleTouchMove(event) {
            if (this.startX >= SWIPE_EDGE) return;

            const deltaX = event.touches[0].clientX - this.startX;
            if (deltaX > 0) {
                event.preventDefault();
                this.currentX = deltaX;
            } else {
                this.currentX = 0;
            }
        },
        handleTouchEnd() {
            if (this.currentX >= 80) {
                this.closeModal();
            } else {
                this.transitionProperty = '.3s all ease';
            }
            this.currentX = 0;
        },
    },

    components: {
        AppSettings,
        IconChevron,
        IconMore,
    },
};
</script>

<style lang="scss">
.app-menu {
    display: flex;
    flex-direction: column;
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100dvh;
    padding-top: 56px;
    background: var(--body-background) !important;
    z-index: 79999;
    box-sizing: border-box;

    &--testnet {
        padding-top: 104px;
    }

    &__scroll {
        flex: 1;
        min-height: 0;
        overflow-y: auto;
        -webkit-overflow-scrolling: touch;
        padding: 12px 12px 16px;
    }
}

// --- Transition: unfold from header ---
.menu-transition-modal-enter-active {
    transition: clip-path .35s cubic-bezier(.4, 0, .2, 1);
}

.menu-transition-modal-leave-active {
    transition: clip-path .25s cubic-bezier(.4, 0, .2, 1);
}

.menu-transition-modal-enter-to,
.menu-transition-modal-leave {
    clip-path: inset(0 0 0 0);
}

.menu-transition-modal-enter,
.menu-transition-modal-leave-to {
    clip-path: inset(0 0 100% 0);
}

// Groups of rows, drawn as cards:
.app-menu-group {
    background: var(--card-background);
    border: 1px solid var(--card-border-color);
    border-radius: 16px;
    overflow: hidden;

    & + & {
        margin-top: 12px;
    }

    &__label {
        margin: 18px 14px 8px;
        font-size: 12px;
        font-weight: 600;
        letter-spacing: .06em;
        text-transform: uppercase;
        color: var(--body-muted-text-color);
    }

    .app-settings {
        padding: 0 4px 4px;
    }
}

.app-menu-item {
    display: flex;
    align-items: center;
    gap: 12px;
    width: 100%;
    height: 52px;
    padding: 0 12px 0 14px;
    box-sizing: border-box;
    font: inherit;
    font-size: 16px;
    font-weight: 500;
    letter-spacing: -0.01em;
    color: var(--body-text-color);
    text-align: left;
    text-decoration: none !important;
    user-select: none;
    -webkit-tap-highlight-color: transparent;
    transition: background-color .12s ease;

    // Every row but the first in the card gets a separator; the folded rows
    // sit inside a wrapper, so a sibling selector alone would miss them.
    & + &,
    .app-menu-more & {
        border-top: 1px solid var(--card-row-separator);
    }

    &:active {
        background: var(--icon-button-hover-background);
    }

    // The «More» row is a button:
    &--toggle {
        border: 0;
        background: none;
        cursor: pointer;
        appearance: none;
        -webkit-appearance: none;
    }

    // Filled glyph on a neutral square; only the active row gets a color.
    &__icon {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 32px;
        height: 32px;
        border-radius: 9px;
        background: var(--card-pill-background);
        color: var(--body-text-color);
        flex-shrink: 0;
        transition: background-color .12s ease, color .12s ease;
        svg {
            width: 18px;
            height: 18px;
            fill: currentColor;
        }
    }

    &__label {
        flex: 1;
        min-width: 0;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }

    &__chevron {
        width: 16px;
        height: 16px;
        color: var(--body-muted-text-color);
        opacity: .6;
        flex-shrink: 0;

        // Turns to point down while the list is open:
        &--toggle {
            transition: transform .25s cubic-bezier(.4, 0, .2, 1);
        }
    }

    &--open .app-menu-item__chevron--toggle {
        transform: rotate(90deg);
    }

    &--active {
        .app-menu-item__icon {
            background: var(--big-blue-button-background);
            color: #fff;
        }
        .app-menu-item__label {
            color: var(--blue-bright);
        }
        .app-menu-item__chevron {
            opacity: 0;
        }
    }
}

// The folded part of the menu: a grid whose single row grows from nothing,
// which animates the height without knowing it.
.app-menu-more {
    display: grid;
    grid-template-rows: 0fr;
    transition: grid-template-rows .25s cubic-bezier(.4, 0, .2, 1);

    &__inner {
        min-height: 0;
        overflow: hidden;
    }

    &--open {
        grid-template-rows: 1fr;
    }
}

@media (prefers-reduced-motion: reduce) {
    .menu-transition-modal-enter-active,
    .menu-transition-modal-leave-active,
    .app-menu-more,
    .app-menu-item__chevron--toggle {
        transition: none;
    }
}
</style>
